import type { InsightsComparisonWindow } from '@/components/ReviewsManagement/InsightsHub/insightsWindow'
import { makeNumber, makeSigned, makeSignedPercent } from '@/helpers/chartFormat'
import { deltaToRatio, direction, isoWeekLabel, monthLabel, roundEstimate, sumInWindow } from '@/helpers/insightsMetrics'

import { metricState, type InsightsMetricState } from './insightsState'
import { visibilityCaption, type CompetitionVisibilityCaption } from './mapCompetitionInsights'
import type { InsightsCategoryTopTenQuery, InsightsProductsBySlugsQuery, MarketInsightsQuery } from '../../reviews.generated'

export type MarketTrendDirection = 'up' | 'down' | 'flat'

export interface MarketTrend {
  value: string
  direction: MarketTrendDirection
}

export interface MarketHero {
  value: string
  trend?: MarketTrend
  month: string
}

export interface MarketWindowHero extends MarketHero {
  caption: CompetitionVisibilityCaption
}

export interface MarketHeroes {
  demand: MarketWindowHero
  density: MarketHero
  supply: MarketHero
  reviews: MarketWindowHero
}

export interface MarketRankPoint {
  value: string
  trend?: MarketTrend
}

export interface MarketRankingData {
  weeks: string[]
  ranks: number[]
  current: MarketRankPoint
  points: MarketRankPoint[]
  dataSince: string | null
}

export interface MarketChartsData {
  months: string[]
  demand: number[]
  supply: number[]
  weeks: string[]
  newPerWeek: number[]
  cumulative: number[]
}

export interface MarketTool {
  slug: string
  name: string
  logoUrl: string | null
}

export interface MarketRatedTool extends MarketTool {
  reviewCount: string | null
  reviewCountValue: number | null
  rating: string | null
}

export interface MarketTopTool extends MarketRatedTool {
  rank: string
}

export interface MarketInsightsView {
  ranking: MarketRankingData
  heroes: MarketHeroes
  charts: MarketChartsData
  newEntrants: MarketRatedTool[]
  hiddenNewEntrants: number
  states: {
    ranking: InsightsMetricState
    demand: InsightsMetricState
    reviewGrowth: InsightsMetricState
    newEntrants: InsightsMetricState
  }
}

export interface MarketTopTen {
  tools: MarketTopTool[]
  state: InsightsMetricState
}

type MarketResults = NonNullable<NonNullable<MarketInsightsQuery['manage']>['insights']>['results']
type MarketResponse = NonNullable<MarketResults[number]['response']>
type RankTrackingResponse = Extract<MarketResponse, { __typename?: 'ManageInsightsRankTrackingResponse' }>
type DemandResponse = Extract<MarketResponse, { __typename?: 'ManageInsightsCategoryDemandResponse' }>
type ReviewGrowthResponse = Extract<MarketResponse, { __typename?: 'ManageInsightsReviewGrowthResponse' }>
type NewEntrantsResponse = Extract<MarketResponse, { __typename?: 'ManageInsightsNewEntrantsResponse' }>
type DemandRow = DemandResponse['rows'][number]
type GrowthRow = ReviewGrowthResponse['rows'][number]
type MarketItem = MarketResults[number]
type MarketProduct = InsightsProductsBySlugsQuery['listProducts'][number]

const MAX_PRODUCT_SLUGS = 100

const formatCount = (value: number | null | undefined, locale: string): string => (value == null ? '—' : makeNumber(locale)(value))

const formatDensity = (pageviews: number | null, tools: number | null | undefined, locale: string): string => {
  if (pageviews == null || !tools) return '—'
  return new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(pageviews / tools)
}

const formatRating = (value: number | null | undefined, locale: string): string =>
  value == null ? '—' : new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)

const roundToDisplayedPercent = (ratio: number): number => Math.round(ratio * 1000) / 1000

const trend = (ratio: number | null, locale: string): MarketTrend | undefined => {
  if (ratio == null) return undefined
  const shown = roundToDisplayedPercent(ratio)
  const direction: MarketTrendDirection = shown > 0 ? 'up' : shown < 0 ? 'down' : 'flat'
  return { value: makeSignedPercent(locale)(ratio), direction }
}

const sumPageviews = (rows: DemandRow[]): number | null => {
  const values = rows.map((row) => row.pageviewsEstimated).filter((value): value is number => value != null)
  return values.length ? values.reduce((sum, value) => sum + value, 0) : null
}

const inWindow = <Row extends { period?: string | null }>(rows: Row[], from: string | null, to: string | null): Row[] =>
  from && to ? rows.filter((row) => row.period && row.period >= from && row.period <= to) : []

const windowState = (item: MarketItem | undefined, windowRows: number, allRows: number): InsightsMetricState => {
  const state = metricState(item, windowRows)
  return state === 'empty' && allRows > 0 ? 'empty-period' : state
}

const demandRatio = (rows: DemandRow[], window: InsightsComparisonWindow, current: number | null): number | null => {
  const { previousStart, previousEnd, months } = window
  const previousRows = inWindow(rows, previousStart, previousEnd)
  if (current == null || previousRows.length !== months) return null
  const previous = sumInWindow(previousRows, (row) => row.pageviewsEstimated, previousStart, previousEnd)
  return previous == null ? null : deltaToRatio(current, current - previous)
}

const hasReviewHistory = (rows: GrowthRow[], before: string | null): boolean => {
  const first = rows[0]
  if (!first?.period || !before) return false
  return first.period <= before || (first.cumulativeReviews ?? 0) - (first.newReviews ?? 0) > 0
}

const monthPeriods = (from: string | null, to: string | null): string[] => {
  if (!from || !to) return []
  const [year, month] = from.split('-').map(Number)
  const monthStart = (offset: number): string => new Date(Date.UTC(year, month - 1 + offset, 1)).toISOString().slice(0, 10)
  const periods: string[] = []
  while (monthStart(periods.length) <= to) periods.push(monthStart(periods.length))
  return periods
}

const growthSeries = (rows: GrowthRow[], window: InsightsComparisonWindow): GrowthRow[] => {
  const periods = monthPeriods(window.start, window.end)
  const byMonth = new Map(rows.map((row) => [row.period?.slice(0, 7), row]))
  let cumulative = rows.filter((row) => row.period && row.period < periods[0]).at(-1)?.cumulativeReviews ?? 0
  return periods.map((period) => {
    const row = byMonth.get(period.slice(0, 7))
    if (row) cumulative = row.cumulativeReviews ?? 0
    return { period, newReviews: row?.newReviews ?? 0, cumulativeReviews: cumulative }
  })
}

const signedCount = (difference: number, locale: string): MarketTrend => ({
  value: difference === 0 ? '±0' : makeSigned(locale)(difference),
  direction: direction(difference),
})

const reviewsHero = (rows: GrowthRow[], window: InsightsComparisonWindow, locale: string): MarketWindowHero => {
  const { start, end, previousStart, previousEnd } = window
  const zeroFill = { missingAsZero: true }
  const pick = (row: GrowthRow) => row.newReviews
  const current = rows.length ? sumInWindow(rows, pick, start, end, zeroFill) : null
  const previous =
    current != null && hasReviewHistory(rows, previousEnd) ? sumInWindow(rows, pick, previousStart, previousEnd, zeroFill) : null
  const trend = previous == null || current == null ? undefined : signedCount(current - previous, locale)
  return {
    value: formatCount(current, locale),
    trend,
    month: monthLabel(inWindow(rows, start, end).at(-1)?.period, locale),
    caption: visibilityCaption(window, trend !== undefined, locale),
  }
}

const densityRatio = (row: DemandRow | undefined): number | null => {
  const { pageviewsEstimated: pageviews, pageviewsEstimatedDeltaPrev: pageviewsDelta } = row ?? {}
  const { toolsWithTraffic: tools, toolsWithTrafficDeltaPrev: toolsDelta } = row ?? {}
  if (pageviews == null || pageviewsDelta == null || !tools || toolsDelta == null) return null
  const previousTools = tools - toolsDelta
  if (!previousTools) return null
  const previousDensity = (pageviews - pageviewsDelta) / previousTools
  if (!previousDensity) return null
  return pageviews / tools / previousDensity - 1
}

const rankTrend = (previous: number | undefined, current: number, locale: string): MarketTrend | undefined => {
  if (previous == null) return undefined
  const places = previous - current
  return { value: makeSigned(locale)(places), direction: direction(places) }
}

const weekStart = (isoDate: string): number => {
  const date = new Date(isoDate)
  return date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7))
}

const dataSince = (windowStart: string | null | undefined, firstPeriod: string | null | undefined, locale: string): string | null => {
  if (!windowStart || !firstPeriod || weekStart(windowStart) >= weekStart(firstPeriod)) return null
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(firstPeriod))
}

const buildRanking = (rank: RankTrackingResponse | undefined, locale: string): MarketRankingData => {
  const rows = (rank?.rows ?? []).filter((row) => row.rankInCategory != null)
  const ranks = rows.map((row) => row.rankInCategory as number)
  const formatRank = (value: number): string => `#${makeNumber(locale)(value)}`
  const current = ranks.at(-1)
  return {
    weeks: rows.map((row) => isoWeekLabel(row.period)),
    ranks,
    current: {
      value: current == null ? '—' : formatRank(current),
      trend: current != null && ranks.length > 1 ? rankTrend(ranks[0], current, locale) : undefined,
    },
    points: ranks.map((value, index) => ({ value: formatRank(value), trend: rankTrend(ranks[index - 1], value, locale) })),
    dataSince: dataSince(rank?.window?.start, rows[0]?.period, locale),
  }
}

const pickResponses = (results: MarketResults) => {
  let rank: RankTrackingResponse | undefined
  let demand: DemandResponse | undefined
  let growth: ReviewGrowthResponse | undefined
  let entrants: NewEntrantsResponse | undefined
  for (const item of results) {
    const response = item.response
    if (!response || item.status !== 200) continue
    if (response.__typename === 'ManageInsightsRankTrackingResponse') rank = response
    else if (response.__typename === 'ManageInsightsCategoryDemandResponse') demand = response
    else if (response.__typename === 'ManageInsightsReviewGrowthResponse') growth = response
    else if (response.__typename === 'ManageInsightsNewEntrantsResponse') entrants = response
  }
  return { rank, demand, growth, entrants }
}

const entrantSlugs = (entrants: NewEntrantsResponse | undefined): string[] =>
  (entrants?.rows ?? []).map((row) => row.toolSlug).filter((slug): slug is string => Boolean(slug))

export const marketProductSlugs = (query: MarketInsightsQuery | null | undefined): string[] => {
  const { entrants } = pickResponses(query?.manage?.insights?.results ?? [])
  return [...new Set(entrantSlugs(entrants))].slice(0, MAX_PRODUCT_SLUGS)
}

export const mapMarketInsights = (
  query: MarketInsightsQuery | null | undefined,
  { locale, products, window }: { locale: string; products: MarketProduct[]; window: InsightsComparisonWindow },
): MarketInsightsView => {
  const results = query?.manage?.insights?.results ?? []
  const { rank, demand, growth, entrants } = pickResponses(results)
  const byMetric = new Map(results.map((item) => [item.metric, item]))

  const allDemandRows = demand?.rows ?? []
  const allGrowthRows = growth?.rows ?? []
  const demandRows = inWindow(allDemandRows, window.start, window.end)
  const growthRows = inWindow(allGrowthRows, window.start, window.end)
  const entrantRows = entrants?.rows ?? []

  const latestDemand = demandRows.at(-1)
  const growthChart = growthSeries(allGrowthRows, window)
  const demandMonth = monthLabel(latestDemand?.period, locale)
  const demandTotal = sumPageviews(demandRows)
  const demandSum = sumInWindow(demandRows, (row) => row.pageviewsEstimated, window.start, window.end)
  const demandTrend = trend(demandRatio(allDemandRows, window, demandSum), locale)
  const ranking = buildRanking(rank, locale)
  const productsBySlug = new Map(products.map((product) => [product.slug, product]))
  const lookupSlugs = new Set(marketProductSlugs(query))
  const allEntrantSlugs = entrantSlugs(entrants)
  const listedEntrantSlugs = allEntrantSlugs.filter((slug) => lookupSlugs.has(slug))

  const states = {
    ranking: metricState(byMetric.get('rank-tracking'), ranking.ranks.length),
    demand: windowState(byMetric.get('category-demand'), demandRows.length, allDemandRows.length),
    reviewGrowth: windowState(byMetric.get('review-growth'), growthRows.length, allGrowthRows.length),
    newEntrants: metricState(byMetric.get('new-entrants'), entrantRows.length),
  }

  return {
    ranking,
    heroes: {
      demand: {
        value: formatCount(roundEstimate(demandSum), locale),
        trend: demandTrend,
        month: demandMonth,
        caption: visibilityCaption(window, demandTrend !== undefined, locale),
      },
      density: {
        value: formatDensity(demandTotal, latestDemand?.toolsWithTraffic, locale),
        trend: trend(densityRatio(latestDemand), locale),
        month: demandMonth,
      },
      supply: {
        value: formatCount(latestDemand?.toolsWithTraffic, locale),
        trend: trend(deltaToRatio(latestDemand?.toolsWithTraffic, latestDemand?.toolsWithTrafficDeltaPrev), locale),
        month: demandMonth,
      },
      reviews: reviewsHero(allGrowthRows, window, locale),
    },
    charts: {
      months: demandRows.map((row) => monthLabel(row.period, locale)),
      demand: demandRows.map((row) => roundEstimate(row.pageviewsEstimated) ?? 0),
      supply: demandRows.map((row) => row.toolsWithTraffic ?? 0),
      weeks: growthChart.map((row) => monthLabel(row.period, locale)),
      newPerWeek: growthChart.map((row) => row.newReviews ?? 0),
      cumulative: growthChart.map((row) => row.cumulativeReviews ?? 0),
    },
    newEntrants: listedEntrantSlugs.map((slug) => {
      const product = productsBySlug.get(slug)
      const rating = product?.averageRecommendationRating
      return {
        slug,
        name: product?.title ?? slug,
        logoUrl: product?.logo?.url ?? null,
        reviewCount: product ? formatCount(product.reviewCount, locale) : null,
        reviewCountValue: product?.reviewCount ?? null,
        rating: rating == null ? null : formatRating(rating, locale),
      }
    }),
    hiddenNewEntrants: allEntrantSlugs.length - listedEntrantSlugs.length,
    states,
  }
}

export const mapCategoryTopTen = (
  query: InsightsCategoryTopTenQuery | null | undefined,
  { locale, failed }: { locale: string; failed: boolean },
): MarketTopTen => {
  const products = query?.exploreProducts?.products ?? []
  return {
    tools: products.map((product, index) => ({
      slug: product.slug,
      name: product.title,
      logoUrl: product.logo?.url ?? null,
      rank: formatCount(index + 1, locale),
      reviewCount: formatCount(product.reviewCount, locale),
      reviewCountValue: product.reviewCount,
      rating: formatRating(product.averageRecommendationRating, locale),
    })),
    state: failed ? 'error' : products.length ? 'ready' : 'empty',
  }
}
